import { useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { TradeLogicTunableManifestV2 } from '@einhaus/project314-types';
import { trpc } from '@/api/trpc';
import { showToast } from '@/components/Layout';
import { Button, Callout, FormCheckbox, Modal } from '@/components/UI';
import { parseBlockingReasons, parseParamVector, type EntryOptimizerCandidate } from '../entryOptimizerUtils';
import type { EntryOptimizerConfirmation } from './ConfirmationsCard';

/**
 * Promote with the Phase 5/6 gate policy: publishing requires passed CV/holdout
 * gates (explicit acknowledgment to override), attaching additionally requires a
 * PASSED confirmation run (its own acknowledgment to override) — EXCEPT for
 * candidates that add a discovered gate, where attach is hard-blocked until a
 * confirmation passes. Shows the old → new diff per tunable and the candidate's
 * confirmation status.
 */
const PromoteCandidateModal = ({
    candidate,
    confirmations,
    jobId,
    manifest,
    onClose
}: {
    candidate: EntryOptimizerCandidate | null;
    confirmations: EntryOptimizerConfirmation[];
    jobId: number;
    manifest: TradeLogicTunableManifestV2 | null;
    onClose: () => void;
}) => {
    const [attachToStrategy, setAttachToStrategy] = useState(false);
    const [acknowledgeUnvalidatedPublish, setAcknowledgeUnvalidatedPublish] = useState(false);
    const [acknowledgeUnconfirmedAttach, setAcknowledgeUnconfirmedAttach] = useState(false);

    const promoteMutation = useMutation({
        ...trpc.entryOptimizer.promoteCandidate.mutationOptions(),
        onError: (error) => showToast(error.message, 'error'),
        onSuccess: (result) => {
            const attached = result.promotion.attachedStrategyId !== null;

            showToast(
                `Published definition ${result.promotion.definitionId} revision ${result.promotion.revision} ` +
                    `(version #${result.promotion.versionId})${attached ? ` and attached to strategy #${result.promotion.attachedStrategyId}` : ''}`,
                'success',
                true
            );

            onClose();
        }
    });

    const changes = useMemo(() => {
        if (!candidate) return [];

        const vector = parseParamVector(candidate);

        return Object.entries(vector).map(([tunableId, value]) => {
            const tunable = manifest?.tunables.find((entry) => entry.tunableId === tunableId);
            const isAdditive = tunable?.origin === 'discovered';

            return {
                tunableId,
                isAdditive,
                label: tunable?.featureLabel ?? tunableId,
                from: isAdditive ? 'no gate' : tunable ? JSON.stringify(tunable.incumbentValue) : '?',
                to: `${isAdditive ? `${tunable?.operator ?? '≤'} ` : ''}${Array.isArray(value) ? `[${value.join(', ')}]` : String(value)}`
            };
        });
    }, [candidate, manifest]);

    const addsStructure = changes.some((change) => change.isAdditive);
    const failsGates = candidate !== null && candidate.passesGates !== 1;
    const blockingReasons = candidate ? parseBlockingReasons(candidate) : [];

    const candidateConfirmations = candidate ? confirmations.filter((entry) => entry.candidateId === candidate.id) : [];
    const hasPassedConfirmation = candidateConfirmations.some((entry) => entry.parityVerdict === 'passed');
    const hasActiveConfirmation = candidateConfirmations.some((entry) => entry.status === 'pending' || entry.status === 'running');

    const needsUnvalidatedAck = failsGates && !acknowledgeUnvalidatedPublish;
    // Additive candidates can NEVER attach unconfirmed (no acknowledgment exists).
    const additiveAttachBlocked = addsStructure && attachToStrategy && !hasPassedConfirmation;
    const needsUnconfirmedAck = !addsStructure && attachToStrategy && !hasPassedConfirmation && !acknowledgeUnconfirmedAttach;

    const canSubmit =
        candidate !== null && !promoteMutation.isPending && !needsUnvalidatedAck && !needsUnconfirmedAck && !additiveAttachBlocked;

    return (
        <Modal isOpen={candidate !== null} title="Advanced: publish source entry" onClose={onClose}>
            <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-2">
                    {changes.map((change) => (
                        <div className="rounded-md border border-zinc-800 bg-black/20 p-3" key={change.tunableId}>
                            <div className="text-sm font-medium text-zinc-100">{change.label}</div>
                            <div className="mt-1 text-sm text-zinc-400">
                                <span className="text-zinc-300">{change.from}</span>
                                <span className="mx-2 text-zinc-500">→</span>
                                <span className="text-cyan-300">{change.to}</span>
                            </div>
                        </div>
                    ))}
                </div>

                {failsGates && (
                    <Callout variant="warning">
                        This setting didn&apos;t pass validation: {blockingReasons.join('; ')}.
                        <FormCheckbox
                            checked={acknowledgeUnvalidatedPublish}
                            label="Save it anyway — I understand it isn't validated"
                            onChange={(event) => setAcknowledgeUnvalidatedPublish(event.target.checked)}
                        />
                    </Callout>
                )}

                <p className="text-sm text-zinc-400">
                    This saves a new version of the entry with these settings. Your strategy keeps using its current entry unless you also
                    attach it below — the new version is available in the Trade Logic editor.
                </p>

                <FormCheckbox
                    checked={attachToStrategy}
                    label="Attach this entry to the source strategy (replaces its current entry)"
                    onChange={(event) => setAttachToStrategy(event.target.checked)}
                />

                {attachToStrategy && hasPassedConfirmation && (
                    <Callout variant="success">
                        A verification run confirmed entry behavior. Trading suitability still requires a separate decision.
                    </Callout>
                )}

                {attachToStrategy && !hasPassedConfirmation && addsStructure && (
                    <Callout variant="error">
                        This adds a new condition to the entry — source attachment requires a passed verification run first and can&apos;t
                        be overridden.{' '}
                        {hasActiveConfirmation
                            ? 'A verification run is in progress; attachment is available once it passes.'
                            : 'Use the Verify button on the results table first.'}
                    </Callout>
                )}

                {attachToStrategy && !hasPassedConfirmation && !addsStructure && (
                    <Callout variant="warning">
                        {hasActiveConfirmation
                            ? 'A verification run is still in progress for this setting.'
                            : 'This setting hasn’t been verified yet. Use the Verify button on the results table to re-test it with a real run.'}
                        <FormCheckbox
                            checked={acknowledgeUnconfirmedAttach}
                            label="Attach anyway — I understand it hasn't been verified"
                            onChange={(event) => setAcknowledgeUnconfirmedAttach(event.target.checked)}
                        />
                    </Callout>
                )}

                <div className="flex justify-end gap-2">
                    <Button variant="outline-dark" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        disabled={!canSubmit}
                        onClick={() => {
                            if (candidate === null) return;

                            promoteMutation.mutate({
                                attachToStrategy,
                                candidateId: candidate.id,
                                jobId,
                                ...(failsGates ? { acknowledgeUnvalidatedPublish } : {}),
                                ...(!addsStructure && attachToStrategy && !hasPassedConfirmation ? { acknowledgeUnconfirmedAttach } : {})
                            });
                        }}
                    >
                        {promoteMutation.isPending ? 'Saving…' : attachToStrategy ? 'Save & Attach to Source' : 'Save New Version'}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default PromoteCandidateModal;
